/**
 * Messages du parrainage sur Mon compte v2 : textes REPRIS À L'IDENTIQUE de la v1
 * (screens/Account/index.tsx : `linkErrorAlert`, `campaignStatusAlert`).
 */
import type { ReferralContextValue } from 'hooks/useReferralContext';

export interface ReferralNotice {
  tone: 'error' | 'warning';
  title: string;
  body: string;
}

/** = index.tsx `linkErrorAlert` : lien d'invitation dont le modèle {{id}} n'a pas été remplacé. */
export function linkErrorNotice(context: Pick<ReferralContextValue, 'linkError'>): ReferralNotice | null {
  if (context.linkError !== 'unsubstituted_template') return null;
  return {
    tone: 'error',
    title: 'Ce lien d’invitation est invalide.',
    body:
      'Le lien que vous avez utilisé n’a pas été personnalisé (le code {{id}} est resté tel quel). Demandez à votre ' +
      'ami de vous renvoyer son lien de parrainage personnel, ou poursuivez votre inscription ci-dessous sans parrainage.',
  };
}

const fmt = (iso: string | null | undefined) => {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' });
};

/** = index.tsx `campaignStatusAlert` : campagne hors de sa fenêtre d'activité. */
export function campaignStatusNotice(
  context: Pick<ReferralContextValue, 'mode' | 'campaignId' | 'campaignStatus' | 'campaign'>,
): ReferralNotice | null {
  if (context.mode !== 'new' || !context.campaignId) return null;
  const status = context.campaignStatus;
  if (status === 'unknown' || status === 'active') return null;

  const startsAt = context.campaign?.startsAt;
  const endsAt = context.campaign?.endsAt;

  switch (status) {
    case 'scheduled': {
      const date = fmt(startsAt);
      return {
        tone: 'warning',
        title: 'Cette campagne de parrainage n’a pas encore commencé.',
        body: date
          ? `Elle débute le ${date}. Vous pouvez créer votre compte dès maintenant, mais la récompense de parrainage ne s’appliquera pas.`
          : 'Vous pouvez créer votre compte dès maintenant, mais la récompense de parrainage ne s’appliquera pas.',
      };
    }
    case 'expired': {
      const date = fmt(endsAt);
      return {
        tone: 'warning',
        title: 'Cette campagne de parrainage est terminée.',
        body: date
          ? `Elle s’est terminée le ${date}. Vous pouvez toujours créer votre compte ci-dessous.`
          : 'Vous pouvez toujours créer votre compte ci-dessous.',
      };
    }
    case 'exhausted':
      return {
        tone: 'warning',
        title: 'Cette campagne de parrainage a atteint son quota.',
        body: 'Toutes les places ont été utilisées. Vous pouvez créer votre compte ci-dessous sans bénéficier de la récompense.',
      };
    case 'inactive':
      return {
        tone: 'warning',
        title: 'Cette campagne de parrainage est désactivée.',
        body: 'Vous pouvez créer votre compte ci-dessous sans bénéficier de la récompense.',
      };
    case 'not_found':
      return {
        tone: 'warning',
        title: 'Ce lien de parrainage n’est pas reconnu.',
        body: 'La campagne associée n’existe pas (ou plus). Vous pouvez créer votre compte ci-dessous sans parrainage.',
      };
    default:
      return null;
  }
}
